import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Autocomplete,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Psychology as PsychologyIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';

import { usePhoneCallService } from '../../services/phoneCallService';
import { useCatalogService } from '../../services/catalogService';
import { useAuth } from '../../context/AuthContext';
import { hasPermission, Permission } from '../../types/auth';
import type { Specialist, SpecialistsResponse, SpecialistsStats, SpecialistContact } from '../../types/phoneCapture';
import SpecialistModal from '../../components/modals/SpecialistModal';

// ✅ ELIMINADO: ServiceArea ya no se usa

interface Specialty {
  id: number;
  name: string;
  code?: string;
}


interface SpecialtyFormItem {
  specialtyId?: number | null;
  name: string;
  isNew?: boolean;
}

interface TemporaryAddressFormData {
  id?: number; // Para edición (si tiene ID, es existente)
  direccion: string;
  horario?: string;
  notas?: string;
}

interface SpecialistFormData {
  fullName: string;
  productArea: string; // ✅ CAMBIADO: usar productArea según especificación
  coverageArea?: any | null; // ✅ NUEVO: Área de cobertura geográfica
  notes: string;
  isActive: boolean;
  modalityCodes: string[]; // Cambiado a códigos como CaptureModal
  specialtyNames: string[]; // Mantener para compatibilidad con el input
  specialties?: SpecialtyFormItem[]; // ✅ NUEVO: Especialidades con IDs preservados
  contacts: SpecialistContact[]; // ✅ NUEVO: Contactos del especialista
  addresses: AddressFormData[]; // ✅ RESTAURADO: Direcciones del especialista
  temporaryAddresses: TemporaryAddressFormData[]; // ✅ NUEVO: Direcciones temporales
}

interface AddressFormData {
  id?: number;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  isPrimary: boolean;
  notes: string;
}

// ✅ ELIMINADO: VALID_PRODUCT_AREAS - no se usa actualmente

const SpecialistsManagement: React.FC = () => {
  console.log('🚀 SpecialistsManagement component mounted');
  
  const { user } = useAuth();
  const {
    getAllSpecialists,
    searchSpecialists,
    getSpecialistsStats,
    createSpecialist,
    updateSpecialist,
    updateSpecialistStatus,
    deleteSpecialist,
    getSpecialistById
  } = usePhoneCallService();
  
  const {
    getSpecialties,
    getAllCatalogs,
    getCoverageAreas
  } = useCatalogService();

  // Estados principales
  const [specialists, setSpecialists] = useState<Specialist[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<SpecialistsStats | null>(null);
  
  // Estados de catálogos
  const [productAreas, setProductAreas] = useState<any[]>([]);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [coverageAreas, setCoverageAreas] = useState<any[]>([]); // ✅ NUEVO: Áreas de cobertura
  const [catalogs, setCatalogs] = useState<any>({});
  const [catalogsLoading, setCatalogsLoading] = useState(false);
  
  // Paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  
  // Filtros y búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [productAreaFilter, setProductAreaFilter] = useState<string>('ALL'); // ✅ CAMBIADO: usar productAreaFilter
  const [coverageAreaFilter, setCoverageAreaFilter] = useState<string>('ALL'); // ✅ NUEVO: Filtro por área de cobertura
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  
  // Modal de especialista
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSpecialist, setEditingSpecialist] = useState<Specialist | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Specialist | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [formData, setFormData] = useState<SpecialistFormData>({
    fullName: '',
    productArea: '', // ✅ CAMBIADO: usar productArea
    coverageArea: null, // ✅ NUEVO: Área de cobertura geográfica
    notes: '',
    isActive: true,
    modalityCodes: [],
    specialtyNames: [], // Cambiado a array de strings
    specialties: [], // ✅ NUEVO: Especialidades con IDs
    contacts: [], // ✅ NUEVO: Contactos del especialista
    addresses: [], // ✅ RESTAURADO: Direcciones del especialista
    temporaryAddresses: [] // ✅ NUEVO: Direcciones temporales
  });
  
  // Notificaciones
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info'
  });

  // Verificar permisos
  if (!user || !hasPermission(user, Permission.MANAGE_SPECIALISTS)) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          No tienes permisos para acceder a esta sección.
        </Alert>
      </Box>
    );
  }

  // Cargar catálogos
  const loadCatalogs = async () => {
    console.log('🔄 loadCatalogs iniciando...');
    try {
      setCatalogsLoading(true);
      console.log('🔄 Cargando catálogos...');
      
      const [specialtiesData, catalogsData, coverageAreasData] = await Promise.all([
        getSpecialties(),
        getAllCatalogs(),
        getCoverageAreas() // ✅ NUEVO: Cargar áreas de cobertura
      ]);
      
      console.log('📊 Datos de catálogos cargados:', {
        specialties: specialtiesData?.length || 0,
        catalogs: Object.keys(catalogsData || {}).length,
        coverageAreas: coverageAreasData?.length || 0
      });
      
      console.log('🎯 Modalidades cargadas:', catalogsData?.DELIVERY_METHOD?.length || 0, 'items');
      
      // ✅ LOG: Mostrar estructura de especialidades recibidas
      console.log('📋 Estructura de especialidades recibidas:', {
        count: specialtiesData?.length || 0,
        sample: specialtiesData?.[0],
        allIds: specialtiesData?.map((s: any) => ({ id: s.id, specialtyId: s.specialtyId, name: s.name }))
      });
      
      // ✅ CAMBIADO: Usar PRODUCT del catálogo según especificación
      setProductAreas(catalogsData?.PRODUCT || []);
      setSpecialties(specialtiesData);
      setCoverageAreas(coverageAreasData || []); // ✅ NUEVO: Guardar áreas de cobertura
      setCatalogs(catalogsData || {});
    } catch (error: any) {
      console.error('❌ Error al cargar catálogos:', error);
      console.error('❌ Error details:', error.message, error.stack);
      setSnackbar({
        open: true,
        message: 'Error al cargar catálogos',
        severity: 'error'
      });
    } finally {
      console.log('🔄 loadCatalogs terminando...');
      setCatalogsLoading(false);
    }
  };

  // Cargar especialistas
  const loadSpecialists = async () => {
    try {
      setLoading(true);
      let response: SpecialistsResponse;
      
      const productArea = productAreaFilter !== 'ALL' ? productAreaFilter : undefined;

      if (searchTerm.trim()) {
        response = await searchSpecialists(searchTerm, page, rowsPerPage);
      } else {
        response = await getAllSpecialists(page, rowsPerPage, 'specialistId', 'asc', productArea);
      }
      
      let filteredSpecialists = response.specialists;
      
      // Filtro de producto en cliente solo durante búsqueda por nombre (el API de búsqueda no lo soporta)
      if (searchTerm.trim() && productAreaFilter !== 'ALL') {
        filteredSpecialists = filteredSpecialists.filter(s => s.productArea === productAreaFilter);
      }
      
      // ✅ NUEVO: Filtro por área de cobertura
      if (coverageAreaFilter !== 'ALL') {
        filteredSpecialists = filteredSpecialists.filter(s => {
          const coverageArea = (s as any).coverageArea;
          return coverageArea && coverageArea.coverageAreaId.toString() === coverageAreaFilter;
        });
      }
      
      if (statusFilter !== 'ALL') {
        const isActive = statusFilter === 'ACTIVE';
        filteredSpecialists = filteredSpecialists.filter(s => (s as any).active === isActive);
      }
      
      setSpecialists(filteredSpecialists);
      setTotalItems(response.totalItems);
    } catch (error: any) {
      console.error('Error al cargar especialistas:', error);
      console.error('Error details:', {
        message: error?.message,
        stack: error?.stack,
        name: error?.name
      });
      
      // ✅ DEBUG: Intentar ver la respuesta raw si es posible
      if (error?.response) {
        console.error('Response status:', error.response.status);
        console.error('Response headers:', error.response.headers);
        console.error('Response data (first 500 chars):', 
          typeof error.response.data === 'string' 
            ? error.response.data.substring(0, 500) 
            : error.response.data
        );
      }
      
      setSnackbar({
        open: true,
        message: 'Error al cargar especialistas',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Cargar estadísticas
  const loadStats = async () => {
    try {
      const statsData = await getSpecialistsStats();
      setStats(statsData);
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  };


  // ✅ DEBUG: Monitorear cambios en modalOpen
  useEffect(() => {
    console.log('🔍 Estado modalOpen cambió:', modalOpen);
    if (modalOpen) {
      console.log('🔍 Modal debería estar visible ahora');
    }
  }, [modalOpen]);

  // ✅ DEBUG: Monitorear cambios en catalogsLoading
  useEffect(() => {
    console.log('🔍 catalogsLoading cambió a:', catalogsLoading);
  }, [catalogsLoading]);

  // Cargar datos iniciales
  useEffect(() => {
    console.log('🔄 useEffect ejecutándose - cargando datos iniciales');
    loadCatalogs();
    loadSpecialists();
    loadStats();
  }, [page, rowsPerPage, searchTerm, productAreaFilter, coverageAreaFilter, statusFilter]);

  // Función para renderizar opciones de catálogo (igual que CaptureModal)
  const renderCatalogOptions = (key: string) => {
    console.log(`🔍 renderCatalogOptions(${key}) ejecutándose:`, {
      catalogs: catalogs,
      keyExists: !!catalogs[key],
      isArray: Array.isArray(catalogs[key]),
      length: catalogs[key]?.length || 0,
      data: catalogs[key]
    });
    
    if (!catalogs[key] || !Array.isArray(catalogs[key])) {
      console.log(`❌ No hay datos para ${key}`);
      return [];
    }

    const options = catalogs[key].map((item: any) => (
      <MenuItem key={item.code} value={item.code}>
        {item.description}
      </MenuItem>
    ));
    
    console.log(`✅ Opciones generadas para ${key}:`, options.length);
    return options;
  };

  // ✅ ELIMINADO: validateSpecialistForm - no se usa actualmente

  // Manejar cambios en el formulario
  const handleFormChange = (field: keyof SpecialistFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // ✅ ELIMINADO: Funciones de direcciones - ahora están en SpecialistModal

  // Abrir modal para crear/editar
  const handleOpenModal = async (specialist?: Specialist) => {
    console.log('🔍 Abriendo modal:', { specialist, isEdit: !!specialist });
    console.log('🔍 Modal actual:', modalOpen);
    
    if (specialist) {
      // ✅ Cargar especialista completo con todas las relaciones antes de abrir el modal
      try {
        setLoading(true);
        console.log('📡 Consultando especialista completo por ID:', specialist.specialistId);
        const fullSpecialist = await getSpecialistById(specialist.specialistId);
        console.log('✅ Especialista completo cargado:', {
          specialistId: fullSpecialist.specialistId,
          fullName: fullSpecialist.fullName,
          specialties: fullSpecialist.specialties?.length || 0,
          contacts: fullSpecialist.contacts?.length || 0,
          addresses: (fullSpecialist as any).addresses?.length || 0,
          temporaryAddresses: (fullSpecialist as any).temporaryAddresses?.length || 0
        });
        
        setEditingSpecialist(fullSpecialist);
        
        console.log('🔍 Datos del especialista desde backend:', {
          specialistId: fullSpecialist.specialistId,
          fullName: fullSpecialist.fullName,
          deliveryMethods: (fullSpecialist as any).deliveryMethods,
          modalities: fullSpecialist.modalities,
          active: (fullSpecialist as any).active,
          contacts: fullSpecialist.contacts,
          specialties: fullSpecialist.specialties,
          addresses: (fullSpecialist as any).addresses,
          temporaryAddresses: (fullSpecialist as any).temporaryAddresses
        });
        
        const mappedData = {
          fullName: fullSpecialist.fullName,
          productArea: fullSpecialist.productArea || '', // ✅ CAMBIADO: usar productArea
          coverageArea: (fullSpecialist as any).coverageArea || null, // ✅ NUEVO: Mapear área de cobertura
          notes: fullSpecialist.notes || '',
          isActive: (fullSpecialist as any).active ?? true, // ✅ CORREGIDO: Usar 'active' del backend
          modalityCodes: (fullSpecialist as any).deliveryMethods ? 
            (fullSpecialist as any).deliveryMethods.split(',').map((code: string) => code.trim()) : 
            fullSpecialist.modalities?.map(m => m.code || m.name) || [],
          specialtyNames: fullSpecialist.specialties?.map(s => s.name) || [], // Mapear nombres
          contacts: fullSpecialist.contacts || [], // ✅ NUEVO: Mapear contactos del especialista
          addresses: (fullSpecialist as any).addresses || [], // ✅ CORREGIDO: Mapear direcciones del especialista
          temporaryAddresses: ((fullSpecialist as any).temporaryAddresses || []).map((ta: any) => ({
            id: ta.id,
            direccion: ta.direccion,
            horario: ta.horario || '',
            notas: ta.notas || ''
          })) // ✅ NUEVO: Mapear direcciones temporales
        };
        
        console.log('📥 Datos mapeados para edición:', {
          ...mappedData,
          modalityCodes: mappedData.modalityCodes,
          modalityCodesLength: mappedData.modalityCodes.length,
          contactsWithIds: mappedData.contacts.filter(c => c.contactId).length,
          contactsWithoutIds: mappedData.contacts.filter(c => !c.contactId).length,
          contactIds: mappedData.contacts.map(c => c.contactId),
          addressesCount: mappedData.addresses.length,
          temporaryAddressesCount: mappedData.temporaryAddresses.length
        });
        setFormData(mappedData);
        
        // ✅ Abrir el modal DESPUÉS de cargar los datos completos
        setModalOpen(true);
        console.log('✅ Modal abierto con datos completos');
      } catch (error) {
        console.error('❌ Error al cargar especialista completo:', error);
        // Fallback: usar el especialista de la lista si falla la carga completa
        setEditingSpecialist(specialist);
        const mappedData = {
          fullName: specialist.fullName,
          productArea: specialist.productArea || '',
          coverageArea: (specialist as any).coverageArea || null,
          notes: specialist.notes || '',
          isActive: (specialist as any).active ?? true,
          modalityCodes: (specialist as any).deliveryMethods ? 
            (specialist as any).deliveryMethods.split(',').map((code: string) => code.trim()) : 
            specialist.modalities?.map(m => m.code || m.name) || [],
          specialtyNames: specialist.specialties?.map(s => s.name) || [],
          contacts: specialist.contacts || [],
          addresses: [],
          temporaryAddresses: ((specialist as any).temporaryAddresses || []).map((ta: any) => ({
            id: ta.id,
            direccion: ta.direccion,
            horario: ta.horario || '',
            notas: ta.notas || ''
          })) // ✅ NUEVO: Mapear direcciones temporales (fallback)
        };
        setFormData(mappedData);
        setModalOpen(true);
      } finally {
        setLoading(false);
      }
    } else {
      setEditingSpecialist(null);
      const newData = {
        fullName: '',
        productArea: productAreas.length > 0 ? productAreas[0].code : '',
        coverageArea: null, // ✅ NUEVO: Sin área de cobertura inicial
        notes: '',
        isActive: true,
        modalityCodes: [],
        specialtyNames: [], // Array vacío para nuevas especialidades
        specialties: [], // ✅ NUEVO: Especialidades con IDs
        contacts: [], // ✅ NUEVO: Contactos vacíos para nuevo especialista
        addresses: [], // ✅ RESTAURADO: Direcciones vacías para nuevo especialista
        temporaryAddresses: [] // ✅ NUEVO: Direcciones temporales vacías para nuevo especialista
      };
      
      console.log('📥 Datos para nuevo especialista:', newData);
      setFormData(newData);
      setModalOpen(true);
    }
  };

  // Cerrar modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingSpecialist(null);
  };

  // ✅ FUNCIÓN PARA MAPEAR DATOS DEL FORMULARIO AL FORMATO DEL BACKEND
  const mapFormDataToBackend = (formData: SpecialistFormData) => {
    // ✅ CORREGIDO: Enviar cadena vacía si no hay modalidades seleccionadas
    const deliveryMethodCodes = formData.modalityCodes.length > 0 
      ? formData.modalityCodes.join(',') 
      : '';
    
    console.log('🔍 Modalidades seleccionadas:', {
      modalityCodes: formData.modalityCodes,
      deliveryMethodCodes: deliveryMethodCodes,
      type: typeof deliveryMethodCodes,
      isEmpty: deliveryMethodCodes === '',
      willSendAs: deliveryMethodCodes === '' ? 'empty string' : 'deliveryMethodCodes'
    });

    console.log('🔍 Estado isActive:', {
      formDataIsActive: formData.isActive,
      type: typeof formData.isActive
    });

    console.log('🔍 Contactos del especialista:', {
      contacts: formData.contacts,
      contactCount: formData.contacts.length,
      contactsWithIds: formData.contacts.filter(c => c.contactId).length,
      contactsWithoutIds: formData.contacts.filter(c => !c.contactId).length,
      contactIds: formData.contacts.map(c => c.contactId)
    });

    // ✅ CORREGIDO: Mapear specialtyNames al formato SpecialtyDTO según especificación
    // Según la especificación, el backend espera un array de objetos SpecialtyDTO:
    // { specialtyId?: number | null, name: string, description?: string, isActive?: boolean }
    
    // ✅ IMPORTANTE: Buscar en ambos catálogos disponibles
    const specialtiesCatalog = catalogs.SPECIALTY || [];
    const allSpecialties = [...specialtiesCatalog, ...specialties].filter(Boolean); // Combinar ambos
    
    // ✅ NUEVA ESTRATEGIA: Usar formData.specialties si está disponible (tiene IDs), sino usar specialtyNames
    let specialtiesData: any[] = [];
    
    if (formData.specialties && Array.isArray(formData.specialties) && formData.specialties.length > 0) {
      // Usar el array specialties que tiene los IDs preservados
      console.log('🔍 Usando formData.specialties con IDs preservados:', formData.specialties);
      
      specialtiesData = formData.specialties.map((s: any) => ({
        specialtyId: s.specialtyId !== undefined ? s.specialtyId : null,
        name: s.name,
        isActive: true
      }));
    } else if (formData.specialtyNames && formData.specialtyNames.length > 0) {
      // Fallback: usar specialtyNames si specialties no está disponible
      console.log('⚠️ Usando specialtyNames (sin IDs):', formData.specialtyNames);
      
      specialtiesData = formData.specialtyNames.map((specialtyName) => {
        // Buscar si existe en catálogo
        const existingFromCatalog = allSpecialties.find((s: any) => 
          s.name && s.name.toLowerCase() === specialtyName.toLowerCase()
        );
        
        const specialtyId = existingFromCatalog ? (existingFromCatalog.specialtyId || existingFromCatalog.id) : null;
        
        console.log(`📝 Mapeando especialidad "${specialtyName}": specialtyId=${specialtyId}`);
        
        return {
          specialtyId: specialtyId,
          name: specialtyName,
          isActive: true
        };
      });
    }
    
    console.log('🔍 Resumen de especialidades mapeadas:', {
      totalCount: specialtiesData.length,
      withId: specialtiesData.filter(s => s.specialtyId !== null).length,
      withoutId: specialtiesData.filter(s => s.specialtyId === null).length,
      specialties: specialtiesData
    });
    
    // ✅ LOG CRÍTICO: Verificar si specialtiesData está vacío
    const hasSpecialties = formData.specialtyNames?.length > 0 || (formData.specialties && formData.specialties.length > 0);
    if (specialtiesData.length === 0 && hasSpecialties) {
      console.error('🚨 ERROR: specialtiesData está vacío pero había especialidades!');
      console.error('🚨 formData.specialtyNames:', formData.specialtyNames);
      console.error('🚨 formData.specialties:', formData.specialties);
    }

    const backendData = {
      fullName: formData.fullName,
      notes: formData.notes,
      // En edición el estatus solo cambia vía PATCH .../status; en alta queda activo.
      active: editingSpecialist
        ? ((editingSpecialist as any).active ?? (editingSpecialist as any).isActive ?? true)
        : true,
      productArea: formData.productArea,
      coverageArea: formData.coverageArea ? { coverageAreaId: formData.coverageArea.coverageAreaId } : null, // ✅ NUEVO: Área de cobertura
      deliveryMethodCodes: deliveryMethodCodes, // ✅ CORREGIDO: Usar 'deliveryMethodCodes' cuando hay modalidades
      specialties: specialtiesData, // ✅ CORREGIDO: Enviar array de objetos SpecialtyDTO
      contacts: formData.contacts.filter(contact => 
        contact.contactType && contact.contactValue
      ), // ✅ NUEVO: Filtrar contactos válidos
      addresses: formData.addresses.filter(address => 
        address.street && address.city && address.state
      ), // ✅ RESTAURADO: Filtrar direcciones válidas
      temporaryAddresses: formData.temporaryAddresses.filter(tempAddr => 
        tempAddr.direccion && tempAddr.direccion.trim().length > 0
      ).map(tempAddr => ({
        id: tempAddr.id, // Incluir ID si existe (para actualización)
        direccion: tempAddr.direccion.trim(),
        horario: tempAddr.horario?.trim() || null,
        notas: tempAddr.notas?.trim() || null
      })) // ✅ NUEVO: Filtrar y mapear direcciones temporales válidas
    };

    console.log('🔍 Datos finales para el backend:', {
      ...backendData,
      deliveryMethodCodes: backendData.deliveryMethodCodes,
      deliveryMethodCodesType: typeof backendData.deliveryMethodCodes,
      specialtiesCount: backendData.specialties.length,
      specialties: backendData.specialties
    });
    
    // ✅ LOG CRÍTICO: Ver JSON completo que se enviará
    console.log('🚨 JSON COMPLETO QUE SE ENVÍA AL BACKEND:', JSON.stringify(backendData, null, 2));

    return backendData;
  };

  // Guardar especialista
  const handleSaveSpecialist = async (modalFormData?: any) => {
    try {
      setLoading(true);
      
      // ✅ USAR DATOS DEL MODAL SI ESTÁN DISPONIBLES
      const dataToSave = modalFormData || formData;
      
      console.log('💾 Guardando especialista:', {
        editingSpecialist: editingSpecialist?.specialistId,
        formData: dataToSave,
        isEdit: !!editingSpecialist
      });
      
      if (editingSpecialist) {
        const updateData = mapFormDataToBackend(dataToSave);
        console.log('📤 Datos para actualizar:', updateData);
        console.log('📤 JSON enviado al backend (UPDATE):', JSON.stringify(updateData, null, 2));
        
        await updateSpecialist(editingSpecialist.specialistId, updateData);
        setSnackbar({
          open: true,
          message: 'Especialista actualizado exitosamente',
          severity: 'success'
        });
      } else {
        const createData = mapFormDataToBackend(dataToSave);
        console.log('📤 Datos para crear:', createData);
        console.log('📤 JSON enviado al backend (CREATE):', JSON.stringify(createData, null, 2));
        
        await createSpecialist(createData);
        setSnackbar({
          open: true,
          message: 'Especialista creado exitosamente',
          severity: 'success'
        });
      }
      
      handleCloseModal();
      loadSpecialists();
      loadStats();
    } catch (error: any) {
      console.error('❌ Error al guardar especialista:', error);
      setSnackbar({
        open: true,
        message: `Error al ${editingSpecialist ? 'actualizar' : 'crear'} especialista: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Desactivar especialista (requiere motivo — guia-front-estatus-especialista.md)
  const handleDeleteSpecialist = (specialist: Specialist) => {
    setDeleteTarget(specialist);
    setDeleteReason('');
  };

  const handleConfirmDeleteSpecialist = async () => {
    if (!deleteTarget) return;
    const reason = deleteReason.trim();
    if (!reason) {
      setSnackbar({
        open: true,
        message: 'El motivo es obligatorio para desactivar',
        severity: 'error',
      });
      return;
    }
    if (reason.length > 500) {
      setSnackbar({
        open: true,
        message: 'El motivo no puede superar 500 caracteres',
        severity: 'error',
      });
      return;
    }
    try {
      setDeleteSaving(true);
      // Preferir PATCH status (equivalente a soft delete con historial)
      await updateSpecialistStatus(deleteTarget.specialistId, { isActive: false, reason });
      setSnackbar({
        open: true,
        message: 'Especialista desactivado exitosamente',
        severity: 'success',
      });
      setDeleteTarget(null);
      setDeleteReason('');
      loadSpecialists();
      loadStats();
    } catch (error: any) {
      console.error('Error al desactivar especialista:', error);
      setSnackbar({
        open: true,
        message: error?.message || 'Error al desactivar especialista',
        severity: 'error',
      });
    } finally {
      setDeleteSaving(false);
    }
  };

  const handleSpecialistStatusChanged = (updated: Specialist) => {
    setEditingSpecialist(updated);
    loadSpecialists();
    loadStats();
  };

  // Manejar cambio de página
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  console.log('🎨 SpecialistsManagement renderizando...', {
    catalogsLoaded: Object.keys(catalogs).length > 0,
    PRODUCT: catalogs.PRODUCT?.length || 0,
    DELIVERY_METHOD: catalogs.DELIVERY_METHOD?.length || 0
  });
  
  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            👨‍⚕️ Gestión de Especialistas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Administra el catálogo de especialistas del sistema
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            console.log('🔍 Click en botón nuevo especialista');
            handleOpenModal();
          }}
          sx={{ borderRadius: 2 }}
        >
          Nuevo Especialista
        </Button>
      </Box>

      {/* Estadísticas */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PersonIcon color="primary" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4" component="div">
                      {stats.totalSpecialists}
                    </Typography>
                    <Typography color="text.secondary">
                      Total Especialistas
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PsychologyIcon color="success" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4" component="div">
                      {stats.psychologists}
                    </Typography>
                    <Typography color="text.secondary">
                      Psicólogos
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <BusinessIcon color="info" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4" component="div">
                      {stats.advisors}
                    </Typography>
                    <Typography color="text.secondary">
                      Asesores
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PersonIcon color="warning" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4" component="div">
                      {stats.others}
                    </Typography>
                    <Typography color="text.secondary">
                      Otros
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filtros y búsqueda */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Buscar especialistas..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Producto</InputLabel>
              <Select
                value={productAreaFilter}
                onChange={(e) => {
                  setProductAreaFilter(e.target.value);
                  setPage(0);
                }}
                label="Producto"
                disabled={catalogsLoading}
              >
                <MenuItem value="ALL">Todas</MenuItem>
                {renderCatalogOptions('PRODUCT')}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Área de Cobertura</InputLabel>
              <Select
                value={coverageAreaFilter}
                onChange={(e) => {
                  setCoverageAreaFilter(e.target.value);
                  setPage(0);
                }}
                label="Área de Cobertura"
              >
                <MenuItem value="ALL">Todas</MenuItem>
                {coverageAreas.map((area: any) => (
                  <MenuItem key={area.coverageAreaId} value={area.coverageAreaId}>
                    {area.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
                label="Estado"
              >
                <MenuItem value="ALL">Todos</MenuItem>
                <MenuItem value="ACTIVE">Activos</MenuItem>
                <MenuItem value="INACTIVE">Inactivos</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadSpecialists}
              disabled={loading}
            >
              Actualizar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabla de especialistas */}
      <Paper>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 480px)', overflow: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Nombre</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Producto</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Área de Cobertura</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Fecha Creación</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }}>Cargando especialistas...</Typography>
                  </TableCell>
                </TableRow>
              ) : specialists.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No se encontraron especialistas.
                  </TableCell>
                </TableRow>
              ) : (
                specialists.map((specialist) => (
                  <TableRow key={specialist.specialistId} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {specialist.fullName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={specialist.productArea || 'N/A'}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    {/* ✅ Columna de Área de Cobertura - disponible en listados */}
                    <TableCell>
                      {(specialist as any).coverageArea ? (
                        <Chip
                          label={(specialist as any).coverageArea.description || (specialist as any).coverageArea.code}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Sin área
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const isActive =
                          (specialist as any).active ?? (specialist as any).isActive ?? true;
                        const reason = (specialist as any).statusChangeReason as string | undefined;
                        const chip = (
                          <Chip
                            label={isActive ? 'Activo' : 'Inactivo'}
                            size="small"
                            color={isActive ? 'success' : 'error'}
                            variant="outlined"
                            icon={isActive ? <CheckCircleIcon /> : <CancelIcon />}
                          />
                        );
                        return reason ? (
                          <Tooltip title={`Motivo: ${reason}`}>
                            <Box component="span">{chip}</Box>
                          </Tooltip>
                        ) : (
                          chip
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(specialist.createdAt).toLocaleDateString('es-MX')}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => {
                              console.log('🔍 Click en botón editar:', specialist);
                              handleOpenModal(specialist);
                            }}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Desactivar">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteSpecialist(specialist)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
          sx={{ 
            borderTop: '1px solid',
            borderColor: 'divider',
            position: 'sticky',
            bottom: 0,
            backgroundColor: 'background.paper',
            zIndex: 1
          }}
        />
      </Paper>

      {/* Modal de especialista */}
        <SpecialistModal
          open={modalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveSpecialist}
          onStatusChanged={handleSpecialistStatusChanged}
          editingSpecialist={editingSpecialist as any}
          loading={loading}
        />

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => {
          if (deleteSaving) return;
          setDeleteTarget(null);
          setDeleteReason('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Desactivar especialista</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Vas a desactivar a <strong>{deleteTarget?.fullName}</strong>. Indica el motivo
            (obligatorio, máx. 500 caracteres).
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Motivo"
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value.slice(0, 500))}
            helperText={`${deleteReason.length}/500`}
            disabled={deleteSaving}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteTarget(null);
              setDeleteReason('');
            }}
            disabled={deleteSaving}
          >
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void handleConfirmDeleteSpecialist()}
            disabled={deleteSaving || !deleteReason.trim()}
          >
            {deleteSaving ? 'Guardando…' : 'Desactivar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal original comentado temporalmente */}
      {false && (
        <Dialog open={false}>
        <DialogTitle>
          {editingSpecialist ? 'Editar Especialista' : 'Nuevo Especialista'}
        </DialogTitle>
        <DialogContent>
          {catalogsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Cargando catálogos...</Typography>
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre Completo"
                value={formData.fullName}
                onChange={(e) => handleFormChange('fullName', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Producto</InputLabel>
                <Select
                  value={formData.productArea}
                  onChange={(e) => handleFormChange('productArea', e.target.value)}
                  label="Producto"
                  disabled={catalogsLoading}
                  onOpen={() => console.log('🔍 Click en combo Área de Producto - Select abierto')}
                  onClose={() => console.log('🔍 Select Área de Producto cerrado')}
                >
                  {catalogs.PRODUCT?.map((item: any) => (
                    <MenuItem key={item.code} value={item.code}>
                      {item.description}
                    </MenuItem>
                  )) || []}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Campos de contacto */}
            {/* ✅ ELIMINADO: Los campos de teléfono y email se manejan ahora en SpecialistContactForm */}
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive ?? true}
                    onChange={(e) => handleFormChange('isActive', e.target.checked)}
                  />
                }
                label="Activo"
              />
            </Grid>
            
            {/* Especialidades con Autocomplete múltiple */}
            <Grid item xs={12}>
              <Autocomplete
                multiple
                freeSolo
                options={specialties.map(s => s.name)}
                value={formData.specialtyNames}
                onChange={(event, newValue) => {
                  handleFormChange('specialtyNames', newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Especialidades"
                    placeholder="Seleccione o escriba una nueva especialidad"
                    helperText="Puede seleccionar especialidades existentes o crear nuevas escribiendo"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      {...getTagProps({ index })}
                      key={index}
                    />
                  ))
                }
              />
            </Grid>
            
            {/* Modalidades */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Modalidades</InputLabel>
                <Select
                  multiple
                  value={formData.modalityCodes}
                  onChange={(e) => handleFormChange('modalityCodes', e.target.value)}
                  label="Modalidades"
                  onOpen={() => console.log('🔍 Click en combo Modalidades - Select abierto')}
                  onClose={() => console.log('🔍 Select cerrado')}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const modality = catalogs.DELIVERY_METHOD?.find((m: any) => m.code === value);
                        return (
                          <Chip
                            key={value}
                            label={modality?.description || value}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {catalogs.DELIVERY_METHOD?.map((item: any) => (
                    <MenuItem key={item.code} value={item.code}>
                      {item.description}
                    </MenuItem>
                  )) || []}
                </Select>
              </FormControl>
            </Grid>
            
            {/* ✅ ELIMINADO: Sección de direcciones - ahora está en SpecialistModal */}
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notas Generales"
                value={formData.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
                multiline
                rows={3}
                placeholder="Notas adicionales sobre el especialista..."
              />
            </Grid>
          </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleSaveSpecialist}
            variant="contained"
            disabled={loading || !formData.fullName.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Guardando...' : editingSpecialist ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
      )}

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SpecialistsManagement;
